import React, { CSSProperties, useRef, useState } from 'react';

import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import CheckBox from 'components/Input/CheckBox/ThemeCheckBox';
import { ReactComponent as BookIcon } from 'assets/icons/book-small.svg';
import { ReactComponent as EditPen } from 'assets/icons/menu/edit.svg';
import { ReactComponent as TrashBin } from 'assets/icons/menu/delete.svg';
import RenderWithRole from 'hoc/renderWithRole';
import EditTaskModalComponent from 'views/Dashboard/components/Upcoming/components/AddTask/EditTaskModal/EditTaskModal';
import { useUpcoming } from 'application/dashboard/upcomings/upcoming.context';
import Icon from 'components/Icon/Icon';
import StyledTooltip from 'components/StyledTooltip/StyledTooltip';

import GradeTask from '../GradeTask/GradeTask';

import './TaskItem.scoped.scss';
import DeleteModal from 'components/DeleteModal/DeleteModal';

interface Props {
  children?: React.ReactNode;
  style?: CSSProperties;
  content?: any;
  hideCompleted?: boolean;
  showAll?: boolean;
  status?: string;
}

const TaskItem: React.FC<Props> = ({ style, content, showAll, status }) => {
  const [modal, toggleModal] = useState<boolean>(false);
  const [editModal, setEditModal] = useState<boolean>(false);
  const { t } = useTranslation('common');
  const { setToggleItem, updateAssignment, getAssignments, deleteAssignment } = useUpcoming();
  const [selected, setSelected] = useState(content.is_completed);

  const addTaskButtonRef = useRef<HTMLDivElement>(null);

  function toggleTheItem() {
    setSelected(!selected);
    const is_completed = content.is_completed;
    setToggleItem({
      id: content.id,
      due_at_filter: content.due_at_filter,
      is_completed: !is_completed,
      completed_at: new Date(),
      show_all: showAll,
    });
  }
  async function callUpdateAssignment(data) {
    await updateAssignment(data);
    showAll ? getAssignments('change', {}) : getAssignments('change', { _eq: showAll });
  }

  async function callDeleteAssignment() {
    await deleteAssignment({ id: content?.id });
    showAll ? getAssignments('change', {}) : getAssignments('change', { _eq: showAll });
    toggleModal(!modal);
  }

  const GradeStudent = RenderWithRole({
    teacher: (
      <div className="action-item show-hide">
        <GradeTask content={content} />
      </div>
    ),
  });

  const DeleteAssignment = RenderWithRole({
    teacher: (
      <div className="action-item pointer no-padding-right task-delete show-hide">
        <StyledTooltip direction="up" tagName="div" content={<p>{t('delete')}</p>}>
          <div className="dropdown-dots pointer">
            <TrashBin onClick={() => toggleModal(!modal)} />
          </div>
        </StyledTooltip>

        <DeleteModal
          openModal={modal}
          handleModal={() => {
            toggleModal(!modal);
          }}
          handleCloseModal={() => {
            toggleModal(!modal);
          }}
          generalMessage={content.title}
          handleDelete={() => callDeleteAssignment()}
        />
      </div>
    ),
  });

  const EditTask = RenderWithRole({
    teacher: (
      <div className="action-item pointer task-edit show-hide">
        <StyledTooltip direction="up" tagName="div" content={<p>{t('edit')}</p>}>
          <div className="dropdown-dots pointer">
            <EditPen onClick={() => setEditModal(true)} />
          </div>
        </StyledTooltip>
      </div>
    ),
  });

  const EditTaskModal = RenderWithRole({
    teacher: (
      <EditTaskModalComponent
        open={editModal}
        content={content}
        sendData={data => callUpdateAssignment(data)}
        onClose={() => {
          setEditModal(false);
        }}
      />
    ),
  });

  function transformDate(date: string) {
    const incomingDate = new Date(date);
    return `${incomingDate.getDate()}/${incomingDate.getMonth() + 1}`;
  }

  return (
    <>
      <div className={`task-item-container ${selected ? 'selected' : ''}`} style={style}>
        <div className="inner-container">
          <div className="task-status">
            <div className="flex align-center">
              <div className={`${status} status`}>
                <h4>{status === 'tomorrow' ? transformDate(content.due_at) : t(status)}</h4>
              </div>
            </div>
          </div>
          <div className="content">
            <div className="row">
              {content?.teaching_structure?.subject_icon ? (
                <Icon name={content?.teaching_structure?.subject_icon} />
              ) : (
                <BookIcon />
              )}
              <div className="flex">
                <p className="upcoming-task-title">
                  {content ? content.title : t('common:no_content_avaliable')} &nbsp;
                  <span className="assignemnt-type">
                    <span className="white-space"></span>
                    <Link to={'/study'} className="pointer ">
                      {t(`${content?.assignment_type?.name}`)}
                    </Link>
                  </span>
                </p>
              </div>
            </div>
            <div className="filler" />

            <div className="actions-container">
              {GradeStudent}
              {EditTask}
              {DeleteAssignment}
              <div className="action-item no-hover-opacity pointer task-check">
                <CheckBox
                  color="orange"
                  round
                  checked={selected}
                  onChange={() => {
                    toggleTheItem();
                  }}
                />
              </div>
            </div>
          </div>
          <div className="popover-edit-container" ref={addTaskButtonRef}>
            {editModal && EditTaskModal}
          </div>
        </div>
      </div>
    </>
  );
};

export default TaskItem;
